import { useEffect, useMemo } from 'react';

import { ICommentGroup } from '@entities/message/model/types';
import { COMMENTS_READ_EVENT_NAME } from '@widgets/Comments/consts';
import {
    addNoCommentsClassForPositionGuid,
    getCommentTypeByActiveTab,
    removeNoCommentsClassForPositionGuid,
    removeUnreadIndicatorForPositionGuid,
} from '@widgets/Comments/lib/polling';

interface IUseClearUnreadIndicatorOnCommentsOpenParams {
    positionGuid: string;
    messagesData: ICommentGroup[];
    isMessagesFetching: boolean;
}

export const useClearUnreadIndicatorOnCommentsOpen = ({
    positionGuid,
    messagesData,
    isMessagesFetching,
}: IUseClearUnreadIndicatorOnCommentsOpenParams): void => {
    const activeCommentType = getCommentTypeByActiveTab();
    const isExcessTab = activeCommentType === 'excessComment';
    const hasAnyComments = useMemo(
        () => messagesData.some((group) => (group.comments?.length || 0) > 0),
        [messagesData]
    );
    const hasUnreadMessages = useMemo(
        () => messagesData.some((group) => group.comments?.some((message) => message.isUnread)),
        [messagesData]
    );
    const readIndicatorGuid = useMemo(() => {
        if (!positionGuid) {
            return '';
        }

        if (!isExcessTab) {
            return positionGuid;
        }

        const normalizedPositionId = String(positionGuid).trim();
        let matchedExcessGuid = '';

        document.querySelectorAll('[data-position-action="openCommentPopup"]').forEach((element) => {
            const positionElement = element.closest('.position');
            const rowPositionId = String(positionElement?.getAttribute('data-position-id') || '').trim();

            if (rowPositionId !== normalizedPositionId) {
                return;
            }

            const rowExcessGuid = String(positionElement?.getAttribute('data-excess-guid') || '').trim();
            matchedExcessGuid = rowExcessGuid || rowPositionId;
        });

        return matchedExcessGuid || normalizedPositionId;
    }, [isExcessTab, positionGuid]);

    useEffect(() => {
        if (!positionGuid || isMessagesFetching) {
            return;
        }

        if (hasAnyComments) {
            removeNoCommentsClassForPositionGuid(positionGuid);
            return;
        }

        addNoCommentsClassForPositionGuid(positionGuid);
    }, [hasAnyComments, isMessagesFetching, positionGuid]);

    useEffect(() => {
        if (!positionGuid || isMessagesFetching) {
            return;
        }

        if (!isExcessTab && !hasUnreadMessages) {
            return;
        }

        removeUnreadIndicatorForPositionGuid(positionGuid);
        window.dispatchEvent(
            new CustomEvent(COMMENTS_READ_EVENT_NAME, {
                detail: { positionGuid: readIndicatorGuid },
            })
        );
    }, [hasUnreadMessages, isExcessTab, isMessagesFetching, positionGuid, readIndicatorGuid]);
};
